/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal'
import { OperationalChipSelectRow } from '../../shared/filters/OperationalChipSelectRow'
import { OperationalFilterPickerRow } from '../../shared/filters/OperationalFilterPrimitives';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { ticketColors } from '../../shared/theme/ticketColors';
import { parsePercentInput } from '../../shared/numeric/localeNumeric';
import { bulkAllCatalogWarning } from '../application/bulkPreviewScope';
import type { CategoryOption } from '../domain/categoryOptions';
import { bulkCoefficientModalStyles as styles } from './bulkCoefficientModal.styles';
import type { BulkApplyTo } from './useBulkCoefficientModal';

type BulkCoefficientFormProps = {
  bulkCategory: string | 'all';
  bulkSearch: string;
  bulkPercentInput: string;
  bulkApplyTo: BulkApplyTo;
  /** Conteo del alcance (con precio / sin precio) — solo flujo de precios. */
  scopeCounts?: { priced: number; unpriced: number } | null;
  categoryOptions: CategoryOption[];
  showApplyToSelector?: boolean;
  showBarcodeCameraButton?: boolean;
  onSearchChange: (value: string) => void;
  onOpenScanner: () => void;
  onSearchSubmitEditing?: () => void;
  autoFocusSearchOnWeb?: boolean;
  onPercentChange: (value: string) => void;
  onApplyToChange: (value: BulkApplyTo) => void;
  categoryPickerOpen: boolean;
  setCategoryPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  categorySearch: string;
  setCategorySearch: (value: string) => void;
  selectedCategoryLabel: string;
  selectedCategoryHint: string;
  visibleCategoryOptions: CategoryOption[];
  selectCategory: (value: string | 'all') => void;
  scopeReady: boolean;
};

export const BulkCoefficientForm = ({
  bulkCategory,
  bulkSearch,
  bulkPercentInput,
  bulkApplyTo,
  scopeCounts = null,
  categoryOptions,
  showApplyToSelector = false,
  showBarcodeCameraButton = true,
  onSearchChange,
  onOpenScanner,
  onSearchSubmitEditing,
  onPercentChange,
  onApplyToChange,
  categoryPickerOpen,
  setCategoryPickerOpen,
  selectedCategoryLabel,
  selectCategory,
  scopeReady: _scopeReady,
}: BulkCoefficientFormProps) => {
  const sheetOptions = useMemo(
    () =>
      categoryOptions
        .filter((opt) => opt.id !== 'all')
        .map((opt) => ({ id: opt.id, name: opt.label })),
    [categoryOptions]
  );

  const pct = parsePercentInput(bulkPercentInput);
  const percentLabel =
    pct == null
      ? null
      : `${pct > 0 ? '+' : ''}${Number.isInteger(pct) ? String(pct) : String(pct)}%`;

  return (
    <View style={styles.ticketFormBody}>
      <View style={styles.ticketField}>
        <OperationalFilterPickerRow
          layout="stack"
          label="Categoría"
          valueLabel={selectedCategoryLabel}
          active={bulkCategory !== 'all'}
          onPress={() => setCategoryPickerOpen(true)}
          accessibilityLabel="Elegir categoría para actualizar en lote"
        />
        {scopeCounts ? (
          <Text style={styles.ticketHint} accessibilityLiveRegion="polite">
            {bulkCategory === 'all'
              ? `Todos con precio (${scopeCounts.priced})`
              : `${scopeCounts.priced} con precio en este alcance`}
            {scopeCounts.unpriced > 0
              ? ` · ${scopeCounts.unpriced} sin precio no se tocan`
              : ''}
          </Text>
        ) : null}
      </View>

      <View style={styles.ticketField}>
        <Text style={styles.ticketFieldLabel}>
          {bulkCategory === 'all' ? 'Filtrar productos' : 'Filtrar productos (opcional)'}
        </Text>
        <View style={styles.ticketInpRow}>
          <TextInput
            style={[styles.ticketInp, styles.ticketInpFlex]}
            value={bulkSearch}
            onChangeText={onSearchChange}
            placeholder="Nombre, SKU, código…"
            placeholderTextColor={ticketColors.ink3}
            onSubmitEditing={onSearchSubmitEditing}
            accessibilityLabel="Filtrar productos para coeficiente en lote"
          />
          {showBarcodeCameraButton ? (
            <TouchableOpacity
              style={styles.ticketScanBtn}
              onPress={onOpenScanner}
              accessibilityRole="button"
              accessibilityLabel="Escanear"
            >
              <Text style={styles.ticketScanGlyph}>▥</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {bulkCategory === 'all' ? (
          <Text style={styles.ticketWarn}>{bulkAllCatalogWarning}</Text>
        ) : (
          <Text style={styles.ticketHint}>Si querés, acotá por nombre o código</Text>
        )}
      </View>

      <View style={styles.ticketField}>
        <Text style={styles.ticketFieldLabel}>Porcentaje</Text>
        <NumericTextInput
          kind="percent"
          style={styles.ticketInp}
          value={bulkPercentInput}
          onChangeText={onPercentChange}
          placeholder="Ej: 10 o -15"
          placeholderTextColor={ticketColors.ink3}
          accessibilityLabel="Porcentaje de ajuste en lote"
        />
        <Text style={styles.ticketHint}>
          Positivo sube el precio. Negativo lo baja. 0 lo deja igual (sirve para revisar el alcance).
        </Text>
      </View>

      {showApplyToSelector ? (
        <View style={styles.ticketField}>
          <Text style={styles.ticketFieldLabel}>Aplicar a</Text>
          <OperationalChipSelectRow
            ticketTone
            value={bulkApplyTo}
            onChange={onApplyToChange}
            options={[
              { value: 'prices', label: 'Precios' },
              { value: 'quantities', label: 'Cantidades de esta sucursal' },
            ]}
          />
        </View>
      ) : null}

      {percentLabel ? (
        <View style={styles.previewReadyCard}>
          <Text style={styles.previewReadyKicker}>Listo para revisar</Text>
          <Text style={styles.previewReadyMeta}>
            {selectedCategoryLabel}
            {bulkSearch.trim() ? ` · filtro “${bulkSearch.trim()}”` : ''} · {percentLabel}. Revisá
            la lista con «Ver vista previa» antes de aplicar.
          </Text>
        </View>
      ) : null}

      <MasterFilterSheetModal
        visible={categoryPickerOpen}
        title="Categoría"
        options={sheetOptions}
        selectedId={bulkCategory === 'all' ? null : bulkCategory}
        allOptionLabel="Todos los productos"
        appearance="ticket"
        onSelect={(id) => selectCategory((id ?? 'all') as string | 'all')}
        onClose={() => setCategoryPickerOpen(false)}
      />
    </View>
  );
};
